import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import Legends from "../../components/Legends";
import { Loader, SegmentedControl, Button, Modal as MantineModal } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "./location.css";
import ControlBar from "../../components/ControlBar";
import DoorControlBar from "../../components/DoorControlBar";
import { Tooltip } from "react-tooltip";
import Modal from "react-modal";
import SensorsTable from "../../components/SensorsTable";
import WindowIcon from "../../svg/windowIcon";
import { Popover } from "@mantine/core";
import SyncIcon from "../../svg/syncIcon";
import { useMsal } from "@azure/msal-react";
import {
  useGetLocationsQuery,
  useGetSensorsQuery,
  useGetDoorSensorsQuery,
  useLazySyncSensorFloorQuery,
  useSyncFlagStatusQuery,
} from "../../redux/slices/api.slice";
import {
  useGetFloorQuery,
  useGetDesksQuery,
  useDoorsReconciliationStatus,
} from "../../hooks/hooks";
import usePermissioned from "../../hooks/usePermissioned";
import { setBreadcrumb } from "../../redux/slices/breadCrumb.slice";
import ReportModal from "../../components/reportModal/ReportModal";

import * as d3 from "d3";
import { floorType } from "../../interfaces/groups";
import PermissionAction from "../../components/permissions/PermissionAction";
import ChairIcon from "../../svg/chairIcon";
import WifiIcon from "../../svg/wifiIcon";
import CupIcon from "../../svg/coffeCupIcon";
import MettingsIcon from "../../svg/meetingIocn";
import PrinterIcon from "../../svg/printerIcon";
import WheelChairIcon from "../../svg/weelchairIcon";
import PetIcon from "../../svg/petIcon";
import CouchIcon from "../../svg/couchIcon";
import MuteIcon from "../../svg/muteIcon";
import { notifications } from "@mantine/notifications";
import { updateSyncFlag } from "../../redux/slices/groups.slice";
import {
  selectDoorsForFloor,
  selectDoorWebsocketOpen,
} from "../../redux/slices/doors.slice";
import DoorOccupancy from "../../components/DoorOccupancy";

// door shapes are detected by id, like desks: door-1, door-2, ... (a legacy
// door-<EUI> tag also matches). The BE door record is joined onto a shape by
// alias — assignment stores the shape id there — or by id for EUI-tagged svgs.
// Figma dedupes duplicate layer names on export (door-1 → door-1_2), so a
// trailing _N is tolerated and stripped to get the canonical shape id.
export const DOOR_SHAPE_ID = /^door-(\d+|[0-9A-F]{8,})(_\d+)?$/i;

const doorShapeKey = (id: string) => id.replace(/_\d+$/, "");

// resolves the paintable element per door shape id. Figma exports can put the
// door id on the wrapping <g> (deduping the vector's id to door-N_2); prefer
// the drawable shape so paint/selection lands on the door outline, not on
// stroke-inheriting children like the numeral.
function collectDoorShapes(container: HTMLElement): Map<string, Element> {
  const byKey = new Map<string, Element>();
  Array.from(container.querySelectorAll('[id^="door-"]'))
    .filter((el) => DOOR_SHAPE_ID.test(el.id))
    .forEach((el) => {
      const key = doorShapeKey(el.id);
      const current = byKey.get(key);
      if (!current || (current.tagName === "g" && el.tagName !== "g")) {
        byKey.set(key, el);
      }
    });
  byKey.forEach((el, key) => {
    if (el.tagName === "g") {
      byKey.set(
        key,
        el.querySelector("path,rect,polygon,circle,ellipse") ?? el
      );
    }
  });
  return byKey;
}

export interface locationProps {
  presentation?: boolean;
}

export function Location() {
  const navigate = useNavigate();
  let { id, GrpId } = useParams();
  const dispatch = useAppDispatch();

  const { desks, floors, syncStatus } = useAppSelector((state) => state.groups);
  const floorDoors = useAppSelector((state) =>
    selectDoorsForFloor(state, id),
  );
  const isDoorWebsocketOpen = useAppSelector((state) =>
    selectDoorWebsocketOpen(state, id),
  );

  // only DO-DILAdmin and DO-EnvironmentManager have door permissions
  const { isPermissioned: canViewDoors } = usePermissioned("R", "doors");
  const { isPermissioned: canEditDoors } = usePermissioned("U", "doors");

  const { isLoading: loadingDesk } = useGetDesksQuery(id, { skip: !id });
  const {
    isLoading: loadingDoors,
    isError: doorsError,
    error: doorsErrorMessage,
    refetch: refetchDoors,
  } = useDoorsReconciliationStatus(id, { skip: !id || !canViewDoors });

  const { data: groups } = useGetLocationsQuery("");
    const [
      syncUpFloor
    ] = useLazySyncSensorFloorQuery();

  const [svg, setSvg] = useState<string>("");
  const { data: unassignedSensorsData } = useGetSensorsQuery({
    id,
    assigned: false,
  });

  const { isLoading } = useGetFloorQuery(id, { skip: !id });
  const [floor, setFloor] = useState<floorType | null>(null);
    const [openSyncModal, setOpenSyncModal] = useState(false);
      const { data: syncFlag, isSuccess } = useSyncFlagStatusQuery("");
  const tvOptions = [
    { name: "TV-Title", desc: "Floorplan with title" },
    { name: "TV-TitleAndLegends", desc: "Floorplan with title and legends" },
    {
      name: "TV-TitleAndLegendsZoom",
      desc: "Floorplan with title and legends zoom",
    },
    { name: "TV", desc: "Floorplan only" },
    { name: "TV-Legends", desc: "Floorplan with legends " },
    {
      name: "TV-NewTitleAndLegends",
      desc: "New floorplan with title and legends ",
    },
    {
      name: "TV-NewTitleAndLegendsWider",
      desc: "New floorplan with title and legends wide ",
    },
  ]; // to be removed once the selection is done

  const amenitiesData = [
    { label: "Wi-Fi Available", icon: WifiIcon },
    { label: "Coffee Space", icon: CupIcon },
    { label: "Meetings Room", icon: MettingsIcon },
    { label: "Printers", icon: PrinterIcon },
    { label: "Wheelchair Accessible", icon: WheelChairIcon },
    { label: "Pet-friendly", icon: PetIcon },
    { label: "Lounge Area", icon: CouchIcon },
    { label: "Quiet Zones", icon: MuteIcon },
  ];
  useEffect(() => {
    if (id) {
      const index = floors.findIndex((floor) => floor?.id === id);
      setFloor(floors[index]);
    }
  }, [floors, id]);

  const [openControlBar, setOpenConrolBar] = useState(false);
  const [modalOpenReserve, setModalOpenReserve] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [availableSensors, setAvailableSensors] = useState([]);
  const [reportDesk, setReportDesk] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string>("")
  const [reportDetails, setReportDetails] = useState<string>("");
  const [editedName, setEditedName] = useState("");
  const [tvSample, setTvSample] = useState(false);
  const [role, setRole] = useState<string[]>([]);

  useEffect(() => {
    setRole(JSON.parse(window.localStorage.getItem("role")));
  }, []);
  const isBasicUser = role.some((r) => r.toLowerCase().includes("basic"));
  // the floor door traffic card is restricted to DIL Admin and Environment
  // Manager only — the same roles that hold the "doors" read permission
  const canViewDoorTraffic =
    canViewDoors &&
    role.some(
      (r) => r === "DO-DILAdmin" || r === "DO-EnvironmentManager",
    );

  useEffect(() => {
    if (unassignedSensorsData) {
      setAvailableSensors(unassignedSensorsData);
    }
  }, [unassignedSensorsData]);

  useEffect(() => {
    const currentLocGrp = groups?.find((l: any) => l.id === floor?.locationID);
   setLocationName(currentLocGrp?.name)
    const crumbs = [
      { name: "Home", route: "/" },
      {
        name: currentLocGrp?.name,
        route: `/Location/${currentLocGrp?.id}`,
      },
      { name: floor?.name, route: "" },
    ];
    dispatch(setBreadcrumb(crumbs));
  }, [groups, dispatch, floor?.name, floor?.locationID]);

  const { accounts } = useMsal();

  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isErrored, setIsErrored] = useState<boolean>(false);
  const [characterCount, setCharacterCount] = useState<number>(0);
  const [availableDesks, setAvailableDesks] = useState<string>("");
  const [deskInfo, setDeskInfo] = useState(null);
  const [view, setView] = useState("Floor Plan");

  const [openDoorBar, setOpenDoorBar] = useState(false);
  const [doorInfo, setDoorInfo] = useState(null);
  const [availableDoorSensors, setAvailableDoorSensors] = useState([]);

  const { data: unassignedDoorSensors } = useGetDoorSensorsQuery(
    { assigned: false },
    { skip: !canViewDoors || !openDoorBar }
  );
  useEffect(() => {
    if (unassignedDoorSensors) setAvailableDoorSensors(unassignedDoorSensors);
  }, [unassignedDoorSensors]);

  const { data: assignedDoorSensors } = useGetDoorSensorsQuery(
    { assigned: true, floorID: id },
    { skip: !id || !canViewDoors }
  );

  // the SVG needs a display status the BE door record doesn't carry: join the
  // assigned sensor (pool id is "door-sensor#<EUI>", door.sensorID is the bare
  // EUI) and derive Occupied/Unoccupied from its state.
  const displayDoors = useMemo(
    () =>
      floorDoors.map((door) => {
        const sensor = assignedDoorSensors?.find(
          (s) => s.sensorID === door.sensorID || s.id === door.sensorID
        );
        return {
          ...door,
          // BE can send negative occupancy, floor it at 0
          current: door.current
            ? {
                ...door.current,
                currentOccupancy: Math.max(
                  0,
                  door.current.currentOccupancy ?? 0
                ),
              }
            : door.current,
          status: door.status
            ? door.status
            : sensor?.state === 1
            ? "Occupied"
            : sensor?.state === 0
            ? "Unoccupied"
            : "No status",
          sensorID: door.sensorID ? door.sensorID : "No sensor",
          timestamp:
            door.status === "Under Maintenance" || door.status === "Blocked"
              ? door.timestamp
              : sensor?.timestamp ?? door.timestamp,
        };
      }),
    [floorDoors, assignedDoorSensors]
  );

  // opening one panel closes the other
  const handleSelectDesk = (d) => {
    setDoorInfo(null);
    setOpenDoorBar(false);
    setDeskInfo(d);
    setOpenConrolBar(true);
  };
  const handleSelectDoor = (d) => {
    setDeskInfo(null);
    setOpenConrolBar(false);
    setDoorInfo(d);
    setOpenDoorBar(true);
  };

   useEffect(() => {
      if (isSuccess && syncFlag === "RUNNING") {
        dispatch(updateSyncFlag({ flag: syncFlag }));
      }
    }, [syncFlag]);
    
    useEffect(() => {
        switch (syncStatus) {
          case "SUCCEEDED": {
            notifications.show({
              title: "Success",
              message: "The sync operation was executed successfully",
              color: "green",
            });
            dispatch(updateSyncFlag({ flag: "IDLE" }));
            break;
          }
          case "FAILED": {
            notifications.show({
              title: "Error",
              message:
                "Something went wrong with the sync operation. Please try again!",
              color: "red",
            });
            dispatch(updateSyncFlag({ flag: "IDLE" }));
            break;
          }
          default: {
            break;
          }
        }
      }, [syncStatus]);
  useEffect(() => {
    setSvg("");
    if (floor?.svgURL) {
      fetch(floor?.svgURL as "RequestInfo | URL")
        .then((res) => res.text())
        .then(setSvg)
        .catch(setIsErrored)
        .then(() => setIsLoaded(true));
    }
  }, [floor?.svgURL]);

  useEffect(() => {
    floor?.name && setEditedName(floor.name);
  }, [floor?.name]);

  useEffect(() => {
    if (view === "Floor Sensors") {
      setOpenConrolBar(false);
      setOpenDoorBar(false);
    }
  }, [view]);

  function reportModal(desk: string | null) {
    setReportDesk(desk);
    setModalOpen(true);
    console.log(accounts);
  }
  function reserveModal() {
    setModalOpenReserve(true);
    console.log(accounts);
  }

  function closeModal() {
    setModalOpen(false);
  }
  function closeModalReserve() {
    setModalOpenReserve(false);
  }

  function handleReportDetailsChange(
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) {
    setReportDetails(e.target.value);
    setCharacterCount(e.target.value.length);
  }
    const handleSyncUpSensorData = async () => {
      const result = await syncUpFloor({floorID: id});
      try {
        //@ts-ignore
        if (result.data) {
          notifications.show({
            title: "Success",
            message: "The sync process has begun successfully.",
            color: "green",
          });
          dispatch(updateSyncFlag({ flag: "RUNNING" }));
        } else {
          notifications.show({
            title: "Error",
            message: "something went wrong , try again!",
            color: "red",
          });
        }
      } catch (error) {
        notifications.show({
          title: "Error",
          message: "something went wrong , try again!",
          color: "red",
        });
      }
      setOpenSyncModal(false);
    };

  if (isLoading) {
    return (
      <div className="flex w-full h-[100vh] justify-center items-center">
        <Loader />
      </div>
    );
  }
  const isFloorPlanView = view === "Floor Plan" && floor?.svgURL;

  return (
    <>
      <div className=" relative  w-full h-full flex lg:flex-row flex-col  ">
        <div
          className={`flex-1 flex justify-center items-center mt-5 flex-col w-full h-full max-w-[1400px] mx-auto   px-6  sm:px-8 `}
        >
          <div className="mt-8 w-full  self-start mx-auto ">
            <div className="flex justify-end mb-6 gap-2">
              <Popover radius="lg" position="bottom" withArrow shadow="md">
                <Popover.Target>
                  
                    <Button
                      radius={"md"}
                      onClick={() => {
                        setTvSample(true);
                      }}
                      leftSection={<WindowIcon />}
                    >
                      View TV Link
                    </Button>
                    
                  
                </Popover.Target>
                <Popover.Dropdown>
                  <div className="p-6 flex flex-col gap-2">
                    {tvOptions.map((option) => {
                      return (
                        <Button
                          key={option.name}
                          radius={"sm"}
                          onClick={() => {
                            window.open(
                              `/${option.name}/${GrpId}/${id}`,
                              "_blank"
                            );
                          }}
                        >
                          {option.desc}
                        </Button>
                      );
                    })}
                  </div>
                </Popover.Dropdown>
              </Popover>
              <Button
                        leftSection={<SyncIcon width={22} height={22} />}
                        radius={"md"}
                        onClick={() => {
                          setOpenSyncModal(true);
                        }}
                        loading={syncStatus === "RUNNING"}
                      >
                        Sync Up
                      </Button>
            </div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-5xl">{floor?.name}</h1>
              {floor?.svgURL && (
                <SegmentedControl
                  size="md"
                  radius="xl"
                  value={view}
                  onChange={setView}
                  className="w-full sm:w-auto"
                  data={["Floor Plan", "Floor Sensors"]}
                />
              )}
            </div>
            {isFloorPlanView && (
              <div>
                <div className="flex gap-4 mb-5 text-grayText3 items-center justify-between flex-wrap">
                  <div className="flex gap-2 items-center">
                    <ChairIcon />
                    <p>
                      <span
                        className={` mr-1 font-md ${
                          availableDesks ? "text-success" : "text-actionLight2"
                        }`}
                      >
                        {availableDesks}
                      </span>
                      of {floor?.capacity} Desks Available
                    </p>
                  </div>
                </div>
                {floor?.amenities?.length > 0 && (
                  <div className="flex gap-2  items-center max-w-full flex-wrap mb-10">
                    {floor.amenities?.map((am) => {
                      const data = amenitiesData.find((el) => el.label === am);
                      const Icon = data.icon;
                      const content = data.label;
                      return (
                        <div className="py-2 px-3  bg-lightGray rounded-2xl border border-solid border-grayText4 flex  gap-3 justify-center items-center">
                          <Icon width="15px" height="15px" />
                          <span className="text-sm text-grayText3">
                            {content}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="w-full">
                  {isBasicUser ? (
                    <Legends role="basic" />
                  ) : (
                    <Legends role="admin" />
                  )}
                </div>
              </div>
            )}
          </div>

          {canViewDoorTraffic && (
            <DoorOccupancy
              doors={floorDoors}
              isWebsocketOpen={isDoorWebsocketOpen}
              isLoading={loadingDoors}
              isError={doorsError}
              error={doorsErrorMessage}
              onRetry={refetchDoors}
              onOpenTrafficGraph={() => navigate("/reports-new#door-traffic")}
            />
          )}

          <PermissionAction operation="U" resource="desks">
            {openControlBar && (
              <div className="  w-full  h-fit lg:hidden block bg-white z-50 shadow ">
                {!deskInfo ? (
                  <h1>loading</h1>
                ) : (
                  <ControlBar
                    setOpenControlBar={setOpenConrolBar}
                    availableSensors={availableSensors}
                    deskInfo={deskInfo}
                    setDeskInfo={setDeskInfo}
                  />
                )}
              </div>
            )}
          </PermissionAction>
          {canEditDoors && openDoorBar && (
            <div className="  w-full  h-fit lg:hidden block bg-white z-50 shadow ">
              {!doorInfo ? (
                <h1>loading</h1>
              ) : (
                <DoorControlBar
                  setOpenControlBar={setOpenDoorBar}
                  availableSensors={availableDoorSensors}
                  doors={displayDoors}
                  doorInfo={doorInfo}
                  setDoorInfo={setDoorInfo}
                />
              )}
            </div>
          )}
          {isFloorPlanView ? (
            isLoaded ? (
              <SvgContent
                svg={svg}
                floor={floor}
                desks={desks}
                doors={displayDoors}
                id={id}
                onSelectDesk={handleSelectDesk}
                onSelectDoor={handleSelectDoor}
                canViewDoors={canViewDoors}
                setAvailableDesks={setAvailableDesks}
                isBasicUser={isBasicUser}
                openControlBar={openControlBar}
                openDoorBar={openDoorBar}
              />
            ) : isErrored ? (
              <div className="flex w-full h-[600px] mb-5 justify-center items-center border border-solid border-[#D5DAE1] rounded-[20px]">
                <p className="text-grayText3">Error fetching floor schema</p>
              </div>
            ) : (
              <div className="flex w-full h-[600px] mb-5 justify-center items-center border border-solid border-[#D5DAE1] rounded-[20px]">
                <p className="text-grayText3">
                  Loading floor schema<span className="loading">...</span>
                </p>
              </div>
            )
          ) : (
            <SensorsTable />
          )}
        </div>

        <PermissionAction operation="U" resource="desks">
          {openControlBar && (
            <div className="w-[400px]  sticky bottom-0 top-0 right-0 lg:block hidden  bg-white z-50 shadow ">
              {!deskInfo ? (
                <h1>loading</h1>
              ) : (
                <ControlBar
                  setOpenControlBar={setOpenConrolBar}
                  availableSensors={availableSensors}
                  deskInfo={deskInfo}
                  setDeskInfo={setDeskInfo}
                />
              )}
            </div>
          )}
        </PermissionAction>

        {canEditDoors && openDoorBar && (
          <div className="w-[400px]  sticky bottom-0 top-0 right-0 lg:block hidden  bg-white z-50 shadow ">
            {!doorInfo ? (
              <h1>loading</h1>
            ) : (
              <DoorControlBar
                setOpenControlBar={setOpenDoorBar}
                availableSensors={availableDoorSensors}
                doors={displayDoors}
                doorInfo={doorInfo}
                setDoorInfo={setDoorInfo}
              />
            )}
          </div>
        )}

        <Tooltip
          id="tooltip"
          className="tooltip-style"
          opacity={1}
          clickable
          variant="light"
          offset={0}
          render={({ content, activeAnchor }) => (
            <>
              <div style={{ padding: "2px 0", fontWeight: "bold" }}>
                {content}
              </div>

              {activeAnchor?.getAttribute("data-tooltip-state") &&
              activeAnchor?.getAttribute("data-tooltip-timestamp") ? (
                <div>
                  {activeAnchor?.getAttribute("data-tooltip-state") && (
                    <span>
                      {`${activeAnchor?.getAttribute(
                        "data-tooltip-state"
                      )} since 
                      ${moment(
                        activeAnchor?.getAttribute("data-tooltip-timestamp")
                      ).fromNow()}`}
                    </span>
                  )}
                </div>
              ) : (
                <div>{activeAnchor?.getAttribute("data-tooltip-state")}</div>
              )}

              <div className="button-div">
                <button onClick={() => reportModal(content)}>
                  Report an issue
                </button>
              </div>
            </>
          )}
        />

        <ReportModal
        locationName={locationName}
        floor={floor}
          modalOpen={modalOpen}
          closeModal={closeModal}
          reportDesk={reportDesk}
        />
          <MantineModal
        opened={openSyncModal}
        onClose={() => setOpenSyncModal(false)}
        size="lg"
        className="p-5"
        withCloseButton={false}
        radius={20}
      >
        <div className="flex flex-col items-center justify-center py-5">
          <div className="flex items-center justify-center rounded-full w-14 h-14 p-2 bg-actionLight">
            <SyncIcon fill="#FB6514" height="80px" />
          </div>
          <h4 className=" text-2xl font-medium mt-2 mb-1 text-grayText7">
            Sync sensor data ?
          </h4>
          <p className="text-lg mt-2  text-grayText5 text-center max-w-[80%] ">
            This Action will synchronize Loriot sensors with DO database
          </p>

          <div className="flex gap-6 mt-6">
            <Button
              color="#D5DAE1"
              onClick={() => setOpenSyncModal(false)}
              variant="default"
              size="md"
              radius={"md"}
            >
              Cancel
            </Button>
            <Button
              color="#FB6514"
              size="md"
              radius={"md"}
              type="submit"
              onClick={handleSyncUpSensorData}
            >
              Confirm
            </Button>
          </div>
        </div>
      </MantineModal>

        <Modal
          isOpen={modalOpenReserve}
          contentLabel="Report an issue"
          onRequestClose={closeModalReserve}
          style={{
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
            },
          }}
        >
          <h2>Reserve this desk for a set of time{reportDesk}</h2>
          <ReserveForm />
        </Modal>
      </div>
    </>
  );
}

function ReserveForm() {
  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];

  // Set the state for the selected start and end dates
  const [startDate, setStartDate] = useState(formattedToday);
  const [endDate, setEndDate] = useState("");

  // Handler for changing the start date
  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStartDate(event.target.value);
  };

  // Handler for changing the end date
  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };
  return (
    <form className="form-reserve">
      <div className="form-group">
        <label htmlFor="startDate">Start Date:</label>
        <input
          type="date"
          id="startDate"
          name="startDate"
          value={startDate}
          onChange={handleStartDateChange}
          min={formattedToday} // Set the minimum date to today
        />
      </div>
      <div className="form-group">
        <label htmlFor="endDate">End Date:</label>
        <input
          type="date"
          id="endDate"
          name="endDate"
          value={endDate}
          onChange={handleEndDateChange}
          min={formattedToday}
        />
      </div>
      <button type="submit">Submit</button>
    </form>
  );
}

export default Location;

const SvgContent = ({
  svg,
  floor,
  desks,
  doors,
  id,
  onSelectDesk,
  onSelectDoor,
  canViewDoors,
  setAvailableDesks,
  isBasicUser,
  openControlBar,
  openDoorBar,
}) => {
  const svgContainer = useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    function handleColors() {
      const filteredDesks = desks?.[id];
      const rects = d3.selectAll("rect");

      rects.each(function (data, index) {
        const rect = d3.select(this);
        const rectID = rect.attr("id");

        if (rectID && rectID.includes("desk")) {
          const id = rectID.split("-")[1];

          rect
            .attr("class", " ")
            .attr("data-tooltip-id", "tooltip")
            .attr("data-tooltip-content", id)
            .attr("fill", "brown")
            .attr("stroke", "none")
            .attr("stroke-width", "0");
        }
      });
      let selectedDesk = null;
      filteredDesks?.forEach((d) => {
        const deskID = d.id.split("-")[0];

        const desk = d3.select(`#desk-${deskID}`);
        // if (!openControlBar) {
        //   desk.attr("stroke", "none");
        //   desk.attr("stroke-width", "0");
        // }

        desk

          .attr("data-tooltip-content", d.alias)
          .attr("data-tooltip-timestamp", d.timestamp)
          .on("click", () => {
            if (selectedDesk !== null) {
              selectedDesk.attr("stroke", "none");
              selectedDesk.attr("stroke-width", "0");
            }

            desk.attr("stroke", "black");
            desk.attr("stroke-width", "0.25%");
            selectedDesk = desk;

            onSelectDesk(d);
          });

        if (
          isBasicUser &&
          (d.status === "Blocked" ||
            d.status === "Under Maintenance" ||
            d.status === "No status")
        ) {
          desk.attr("data-tooltip-state", "Blocked");
        } else {
          desk.attr("data-tooltip-state", d.status);
        }

        if (!isBasicUser) {
          if (d.status.toLowerCase() === "unoccupied") {
          
            if (d.timestamp) {
              const timestamp = moment(d.timestamp);
              const currentDate = moment();
              const duration = moment
                .duration(currentDate.diff(timestamp))
                .asMinutes();
              if (duration >= 45) {
                desk.attr("fill", "green");
              } else {
                desk.attr("fill", "orange");
              }
            } else {
              desk.attr("fill", "green");
            }

            return;
          } else if (d.status === "Occupied") {
            desk.attr("fill", "red");
            return;
          } else if (d.status === "Blocked") {
            desk.attr("fill", "blue");
            return;
          } else if (d.status === "Under Maintenance") {
            desk.attr("fill", "black");
            return;
          } else {
            desk.attr("fill", "pink");

            // doesn't have a sensor

            return;
          }
        } else {
          if (d.status === "Unoccupied") {
            if (d.timestamp) {
              const timestamp = moment(d.timestamp);
              const currentDate = moment();
              const duration = moment
                .duration(currentDate.diff(timestamp))
                .asHours();

              if (duration >= 1) {
                desk.attr("fill", "green");
              } else {
                desk.attr("fill", "orange");
              }
            } else {
              desk.attr("fill", "green");
            }

            return;
          } else if (d.status === "Occupied") {
            desk.attr("fill", "red");
            return;
          } else {
            desk.attr("fill", "blue");
            return;
          }
        }
      });

      setAvailableDesks(
        `${floor?.capacity - (floor?.occupied + floor?.unavailable)}`
      );
    }

    handleColors();
  }, [desks, floor, id]);

  useEffect(() => {
    const filteredDesks = desks?.[id];
    if (!openControlBar) {
      filteredDesks?.forEach((d) => {
        const deskID = d.id.split("-")[0];

        const desk = d3.select(`#desk-${deskID}`);
        desk.attr("stroke", "none");
        desk.attr("stroke-width", "0");
      });
    }
  }, [openControlBar, desks, id]);

  // like desks, doors are driven by the svg: every shape whose id matches
  // DOOR_SHAPE_ID is a door. A shape with no BE record yet is shown as
  // unassigned and stays clickable so a sensor can be assigned to it; the
  // assign call persists the shape id in the record's alias, which is the
  // join key on later loads (record.id covers legacy door-<EUI> tags).
  React.useEffect(() => {
    const container = svgContainer.current;
    if (!container) return;
    // users without door access must not see the door shapes at all
    if (!canViewDoors) {
      collectDoorShapes(container).forEach((el) => {
        (el.closest('g[id^="door-"]') ?? el).setAttribute("display", "none");
      });
      return;
    }
    const filteredDoors = doors;
    let selectedDoor = null;

    collectDoorShapes(container).forEach((el, key) => {
      // overlays inside the door group (the numeral) must not swallow clicks
      const group = el.closest('g[id^="door-"]');
      // permissions resolve async — clear the hide applied before roles loaded
      (group ?? el).removeAttribute("display");
      if (group && group !== el) {
        Array.from(group.children).forEach((child) => {
          if (child !== el && !child.contains(el)) {
            child.setAttribute("pointer-events", "none");
          }
        });
      }

      const record = filteredDoors?.find(
        (r) => r.alias === key || r.id === key
      );
      const d = record ?? {
        id: "",
        floorID: id,
        sensorID: "",
        alias: key,
        status: "Unassigned",
        timestamp: "",
      };

      const door = d3.select(el);
      door
        .attr("data-tooltip-id", "tooltip")
        .attr("data-tooltip-content", d.alias)
        .attr("data-tooltip-timestamp", d.timestamp)
        .attr("data-tooltip-state", d.status)
        .on("click", () => {
          if (selectedDoor !== null) {
            selectedDoor.attr("stroke", "none");
            selectedDoor.attr("stroke-width", "0");
          }
          door.attr("stroke", "black");
          door.attr("stroke-width", "0.25%");
          selectedDoor = door;

          onSelectDoor(d);
        });

      if (!record) {
        door.attr("fill", "brown");
      } else if (d.status === "active") {
        door.attr("fill", "green");
      } else if (d.status.toLowerCase() === "unoccupied") {
        if (d.timestamp) {
          const duration = moment
            .duration(moment().diff(moment(d.timestamp)))
            .asMinutes();
          door.attr("fill", duration >= 45 ? "green" : "orange");
        } else {
          door.attr("fill", "green");
        }
      } else if (d.status === "Occupied") {
        door.attr("fill", "red");
      } else if (d.status === "Blocked") {
        door.attr("fill", "blue");
      } else if (d.status === "Under Maintenance") {
        door.attr("fill", "black");
      } else {
        door.attr("fill", "pink");
      }
    });
  }, [svg, doors, floor, id, canViewDoors]);

  useEffect(() => {
    if (!canViewDoors || openDoorBar) return;
    const container = svgContainer.current;
    if (!container) return;
    collectDoorShapes(container).forEach((el) => {
      const door = d3.select(el);
      door.attr("stroke", "none");
      door.attr("stroke-width", "0");
    });
  }, [openDoorBar, doors, id, canViewDoors]);

  return (
    <div
      ref={svgContainer}
      className="svgWrapper w-full h-full mb-10"
      dangerouslySetInnerHTML={{
        __html: svg,
      }}
    />
  );
};
